// Environment-based configuration
// Keys off NEXT_PUBLIC_ENVIRONMENT (not NEXT_PUBLIC_APP_ENV)
//
// Override URLs via environment variables (useful for PR preview environments):
//   NEXT_PUBLIC_API_BASE_URL     - overrides apiBaseUrl (legacy API)
//   NEXT_PUBLIC_NEW_API_URL      - overrides newApiUrl (new API)
//   NEXT_PUBLIC_PORTAL_BASE_URL  - overrides portalBaseUrl (advocate portal self-URL,
//                                  used for federated logout returnTo)
//
// PR Preview Detection:
//   When deployed as a Vercel PR preview, automatically uses PR-specific backends
//   based on NEXT_PUBLIC_VERCEL_ENV and NEXT_PUBLIC_VERCEL_GIT_PULL_REQUEST_ID

const baseConfig = {
  production: {
    apiBaseUrl: 'https://api.joinzealot.com',
    newApiUrl: 'https://api2.joinzealot.com',
    portalBaseUrl: 'https://{brand}.joinzealot.com',
  },
  staging: {
    apiBaseUrl: 'https://stagingapi.joinzealot.com',
    newApiUrl: 'https://api2.staging.joinzealot.com',
    portalBaseUrl: 'https://{brand}.staging.joinzealot.com',
  },
  develop: {
    apiBaseUrl: 'https://api.develop.joinzealot.com',
    newApiUrl: 'https://api2.develop.joinzealot.com',
    portalBaseUrl: 'https://{brand}.develop.joinzealot.com',
  },
  local: {
    apiBaseUrl: 'http://localhost:8080', // Legacy API
    newApiUrl: 'http://localhost:8000', // New API via localhost
    portalBaseUrl: 'http://localhost:3001',
  },
  test: {
    apiBaseUrl: 'http://localhost:8080', // Legacy API (e2e isolated stack)
    newApiUrl: 'http://localhost:8000', // New API via localhost
  },
};

// Alias for backward compatibility
baseConfig.development = baseConfig.develop;

// why: an unset or unknown NEXT_PUBLIC_ENVIRONMENT used to index this map to `undefined`,
// why: and every consumer then fell through to its own `|| 'http://localhost:8000'` default.
// why: A deployment built without the variable therefore looked healthy and talked to nothing.
// why: Fail at import instead — a broken build is visible, a silent localhost is not.
// why: Own-key, not truthiness: `baseConfig['constructor']` is inherited and truthy,
// why: so a plain lookup would wave through a name this map does not define.
export const assertKnownEnvironment = (environment) => {
  if (Object.prototype.hasOwnProperty.call(baseConfig, environment)) return environment;
  throw new Error(
    `NEXT_PUBLIC_ENVIRONMENT is ${environment === undefined ? 'unset' : `"${environment}"`}; ` +
      `expected one of: ${Object.keys(baseConfig).join(', ')}. ` +
      'Set it in the deployment environment (Vercel project/environment settings) or in .env.local.',
  );
};

// why: skipped under vitest, which legitimately has no deployment environment. Forcing
// why: one changed API_BASE_URL from undefined to http://localhost:8080 for every suite and
// why: perturbed render timing in unrelated tests. Keyed on VITEST, not NODE_ENV: `next
// why: build` honours a pre-set NODE_ENV, and NODE_ENV lives in the Doppler configs, so a
// why: NODE_ENV check was one edit away from disabling this guard in a real build.
if (!process.env.VITEST) assertKnownEnvironment(process.env.NEXT_PUBLIC_ENVIRONMENT);

// Detect PR preview environment (Vercel auto-exposes these as NEXT_PUBLIC_*)
const isVercelPreview = process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview';
const prNumber = process.env.NEXT_PUBLIC_VERCEL_GIT_PULL_REQUEST_ID;

// Determine URL overrides - PR-specific URLs take precedence, then explicit overrides
const getPrApiBaseUrl = () =>
  isVercelPreview && prNumber ? `https://zealot-legacy-api-pr-${prNumber}.onrender.com` : null;
const getPrNewApiUrl = () => (isVercelPreview && prNumber ? `https://zealot-api-pr-${prNumber}.onrender.com` : null);
// On Vercel PR previews the portal has no `{brand}` subdomain template, so without
// an explicit override `buildLogoutOptions` would fall back to the develop/prod
// template and bounce the user off the preview onto develop/prod after logout.
// Vercel projects can set NEXT_PUBLIC_PORTAL_BASE_URL on previews to keep logout local.
const getPrPortalBaseUrl = () => (isVercelPreview ? process.env.NEXT_PUBLIC_PORTAL_BASE_URL || null : null);

const apiBaseUrlOverride = getPrApiBaseUrl() || process.env.NEXT_PUBLIC_API_BASE_URL;
const newApiUrlOverride = getPrNewApiUrl() || process.env.NEXT_PUBLIC_NEW_API_URL;
const portalBaseUrlOverride = getPrPortalBaseUrl() || process.env.NEXT_PUBLIC_PORTAL_BASE_URL;

const config = Object.fromEntries(
  Object.entries(baseConfig).map(([env, urls]) => [
    env,
    {
      apiBaseUrl: apiBaseUrlOverride || urls.apiBaseUrl,
      newApiUrl: newApiUrlOverride || urls.newApiUrl,
      portalBaseUrl: portalBaseUrlOverride || urls.portalBaseUrl,
    },
  ]),
);

export default config;
